/**
 * ET - Testimonials slider (sections/et-collection-testimonials.liquid · Swiper 11 via window.etLoadSwiper)
 *
 * Markup contract
 *   Root      [data-et-testimonials][data-section-id]
 *   Swiper    [data-et-testimonials-swiper] > .swiper-wrapper > .swiper-slide[data-et-testimonials-slide]
 *   Scrollbar [data-et-testimonials-scrollbar] (Swiper Scrollbar module inside .et-slider-progress)
 *
 * Slide widths live in CSS (3 per view ≥1200px, 2 per view 990–1199px, 308px tablet, 280px mobile), so Swiper runs with
 * slidesPerView 'auto' at every breakpoint and simply follows the stylesheet on resize. watchOverflow locks the slider
 * (and hides the progress pill) when every card fits. Initialises on load and shopify:section:load, destroys on
 * shopify:section:unload and slides to the selected card on shopify:block:select.
 *
 * Progress thumb: Swiper's scrollbar dragSize follows the CSS custom property --et-testimonials-progress-thumb (39px on
 * mobile per Figma 54565:99468), read on init and before every resize; when unset the thumb is sized automatically.
 *
 * Keyboard: the swiper container is a tab stop (tabindex="0" in the markup) while it can move; arrow keys, Home and End
 * move it only while it has focus (a delegated keydown listener, not Swiper's Keyboard module, which listens on the whole
 * document and would also move every other slider in view). The tab stop is removed while the slider is locked.
 *
 * Autoplay (section setting, off by default): root [data-autoplay="true"][data-autoplay-speed="<ms>"]. Pauses on hover,
 * while focus is inside the section and while a block is selected in the theme editor; never runs for visitors who
 * prefer reduced motion. At the last card it rewinds to the first.
 */
(function () {
  'use strict';

  const READY_ATTRIBUTE = 'data-et-testimonials-ready';
  if (document.documentElement.hasAttribute(READY_ATTRIBUTE)) return;
  document.documentElement.setAttribute(READY_ATTRIBUTE, '');

  const ROOT_SELECTOR = '[data-et-testimonials]';
  const SWIPER_SELECTOR = '[data-et-testimonials-swiper]';
  const SLIDE_SELECTOR = '[data-et-testimonials-slide]';
  const SPACE_BETWEEN = 16;
  const PENDING = 'pending';
  const THUMB_PROPERTY = '--et-testimonials-progress-thumb';
  const DEFAULT_AUTOPLAY_DELAY = 5000;

  const instances = new Map(); // root element → Swiper instance or PENDING
  const pendingSlides = new Map(); // root element → slide index requested before Swiper was ready

  function loadSwiper() {
    if (typeof window.etLoadSwiper === 'function') return window.etLoadSwiper();
    return Promise.resolve(typeof window.Swiper === 'function' ? window.Swiper : null);
  }

  function rootsIn(scope) {
    const context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    const roots = Array.from(context.querySelectorAll(ROOT_SELECTOR));
    if (context instanceof Element && context.matches(ROOT_SELECTOR)) roots.unshift(context);
    return roots;
  }

  function destroy(root) {
    const instance = instances.get(root);
    instances.delete(root);
    pendingSlides.delete(root);
    if (isReady(instance)) instance.destroy(true, true);
  }

  function isReady(instance) {
    return Boolean(instance) && instance !== PENDING && !instance.destroyed;
  }

  /* Fixed thumb width from CSS (e.g. 39px on mobile) or Swiper's proportional 'auto' */
  function thumbSize(scrollbarEl) {
    if (!scrollbarEl) return 'auto';
    const value = parseFloat(window.getComputedStyle(scrollbarEl).getPropertyValue(THUMB_PROPERTY));
    return Number.isFinite(value) && value > 0 ? value : 'auto';
  }

  function syncThumbSize(swiper) {
    if (!swiper || !swiper.params || !swiper.params.scrollbar || !swiper.params.scrollbar.el) return;
    const scrollbarEl = swiper.scrollbar && swiper.scrollbar.el ? swiper.scrollbar.el : null;
    swiper.params.scrollbar.dragSize = thumbSize(scrollbarEl);
  }

  function autoplayOptions(root) {
    if (root.dataset.autoplay !== 'true') return false;
    const reducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return false;
    const delay = parseInt(root.dataset.autoplaySpeed, 10);
    return {
      delay: Number.isFinite(delay) && delay > 0 ? delay : DEFAULT_AUTOPLAY_DELAY,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    };
  }

  function hasAutoplay(instance) {
    return isReady(instance) && Boolean(instance.autoplay) && Boolean(instance.params.autoplay && instance.params.autoplay.enabled);
  }

  function stopAutoplay(root) {
    const instance = instances.get(root);
    if (hasAutoplay(instance) && instance.autoplay.running) instance.autoplay.stop();
  }

  function startAutoplay(root) {
    const instance = instances.get(root);
    if (hasAutoplay(instance) && !instance.autoplay.running) instance.autoplay.start();
  }

  /* Only a slider that can move is a tab stop */
  function syncFocusable(swiper) {
    if (!swiper || !swiper.el) return;
    if (swiper.isLocked) {
      if (swiper.el === document.activeElement) swiper.el.blur();
      swiper.el.removeAttribute('tabindex');
    } else {
      swiper.el.setAttribute('tabindex', '0');
    }
  }

  function create(Swiper, root) {
    const container = root.querySelector(SWIPER_SELECTOR);
    if (!container || !container.querySelector(SLIDE_SELECTOR)) {
      instances.delete(root);
      return;
    }

    const scrollbarEl = root.querySelector('[data-et-testimonials-scrollbar]');
    const options = {
      slidesPerView: 'auto',
      spaceBetween: SPACE_BETWEEN,
      watchOverflow: true,
      grabCursor: true,
      initialSlide: pendingSlides.get(root) || 0,
      autoplay: autoplayOptions(root),
      a11y: { enabled: true },
      on: {
        lock: syncFocusable,
        unlock: syncFocusable,
        beforeResize: syncThumbSize
      }
    };

    if (scrollbarEl) {
      options.scrollbar = {
        el: scrollbarEl,
        draggable: true,
        snapOnRelease: true,
        dragSize: thumbSize(scrollbarEl)
      };
    }

    pendingSlides.delete(root);

    try {
      const instance = new Swiper(container, options);
      instances.set(root, instance);
      syncFocusable(instance);
    } catch (error) {
      instances.delete(root);
      console.error('[ET testimonials] Swiper init failed', error);
    }
  }

  function init(root) {
    if (!root.isConnected || instances.has(root)) return;
    instances.set(root, PENDING);

    loadSwiper().then((Swiper) => {
      if (instances.get(root) !== PENDING) return;
      if (!Swiper || !root.isConnected) {
        instances.delete(root);
        return;
      }
      create(Swiper, root);
    });
  }

  function initAll(scope) {
    instances.forEach((_instance, root) => {
      if (!root.isConnected) destroy(root);
    });
    rootsIn(scope).forEach(init);
  }

  function onSectionUnload(event) {
    rootsIn(event.target).forEach(destroy);
  }

  function onBlockSelect(event) {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const root = slide ? slide.closest(ROOT_SELECTOR) : null;
    if (!root) return;

    const index = Array.from(slide.parentElement.children).indexOf(slide);
    const instance = instances.get(root);

    if (isReady(instance)) {
      stopAutoplay(root);
      instance.slideTo(index);
      return;
    }

    pendingSlides.set(root, index);
    init(root);
  }

  function onBlockDeselect(event) {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const root = slide ? slide.closest(ROOT_SELECTOR) : null;
    if (root && !root.contains(document.activeElement)) startAutoplay(root);
  }

  function onFocusIn(event) {
    const root = event.target instanceof Element ? event.target.closest(ROOT_SELECTOR) : null;
    if (root) stopAutoplay(root);
  }

  function onFocusOut(event) {
    const root = event.target instanceof Element ? event.target.closest(ROOT_SELECTOR) : null;
    if (!root || (event.relatedTarget instanceof Node && root.contains(event.relatedTarget))) return;
    startAutoplay(root);
  }

  function onKeydown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!(event.target instanceof Element) || !event.target.matches(SWIPER_SELECTOR)) return;

    const root = event.target.closest(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    if (!isReady(instance) || instance.isLocked) return;

    const forward = instance.rtlTranslate ? 'slidePrev' : 'slideNext';
    const backward = instance.rtlTranslate ? 'slideNext' : 'slidePrev';

    switch (event.key) {
      case 'ArrowRight':
        instance[forward]();
        break;
      case 'ArrowLeft':
        instance[backward]();
        break;
      case 'Home':
        instance.slideTo(0);
        break;
      case 'End':
        instance.slideTo(instance.slides.length - 1);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  document.addEventListener('keydown', onKeydown);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:select', onBlockSelect);
  document.addEventListener('shopify:block:deselect', onBlockDeselect);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
})();
